/**
 * Completeness checks for Ready for Publishing.
 * Returns a detailed multi-line error when required fields are missing.
 */
import { CONTENT_LOCALES, type ContentLocale, LOCALE_LABELS } from "../constants";
import { isValidLocaleMapShape } from "./localizationValidation";

export type CompletenessIssue = string;

function nonEmptyString(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

function requireLocaleMapStrings(
  fieldLabel: string,
  value: unknown,
  issues: CompletenessIssue[],
): void {
  if (!isValidLocaleMapShape(value)) {
    issues.push(
      `${fieldLabel}: must include exactly locales ${CONTENT_LOCALES.join(", ")}.`,
    );
    return;
  }
  const map = value as Record<string, unknown>;
  for (const locale of CONTENT_LOCALES) {
    if (!nonEmptyString(map[locale])) {
      issues.push(`${fieldLabel} (${LOCALE_LABELS[locale]}): required text is missing.`);
    }
  }
}

function requireDestinationLocaleContent(
  content: unknown,
  issues: CompletenessIssue[],
): void {
  if (!isValidLocaleMapShape(content)) {
    issues.push(
      `Content: must include exactly locales ${CONTENT_LOCALES.join(", ")}.`,
    );
    return;
  }
  const map = content as Record<string, Record<string, unknown>>;
  for (const locale of CONTENT_LOCALES) {
    const block = map[locale];
    const label = LOCALE_LABELS[locale as ContentLocale];
    if (!block || typeof block !== "object") {
      issues.push(`Content (${label}): missing content block.`);
      continue;
    }
    if (!nonEmptyString(block.headline)) {
      issues.push(`Content (${label}): headline is required.`);
    }
    if (!nonEmptyString(block.intro)) {
      issues.push(`Content (${label}): intro is required.`);
    }
  }
}

export function collectDestinationCompletenessIssues(
  values: Record<string, unknown>,
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = [];
  if (!nonEmptyString(values.name)) {
    issues.push("Name: required.");
  }
  requireDestinationLocaleContent(values.content, issues);
  const images = values.images;
  if (!Array.isArray(images) || images.length === 0) {
    issues.push("Images: at least one image is required.");
  }
  return issues;
}

export function collectGuideCompletenessIssues(
  values: Record<string, unknown>,
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = [];
  if (!nonEmptyString(values.slug)) {
    issues.push("Slug: required.");
  }
  requireLocaleMapStrings("Title", values.title, issues);
  requireLocaleMapStrings("Description", values.description, issues);
  return issues;
}

export function collectArticleCompletenessIssues(
  values: Record<string, unknown>,
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = [];
  if (!nonEmptyString(values.slug)) {
    issues.push("Slug: required.");
  }
  requireLocaleMapStrings("Title", values.title, issues);
  requireLocaleMapStrings("Content", values.content, issues);
  return issues;
}

export function collectAuthorCompletenessIssues(
  values: Record<string, unknown>,
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = [];
  if (!nonEmptyString(values.name)) {
    issues.push("Name: required.");
  }
  if (!nonEmptyString(values.slug)) {
    issues.push("Slug: required.");
  }
  requireLocaleMapStrings("Bio", values.bio, issues);
  return issues;
}

export function collectSiteChromeCompletenessIssues(
  values: Record<string, unknown>,
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = [];
  requireLocaleMapStrings("Document title suffix", values.document_title_suffix, issues);
  requireLocaleMapStrings("Footer brand tagline", values.footer_tagline, issues);

  const navFields: Array<[string, unknown]> = [
    ["Main navigation", values.main_nav],
    ["Footer navigation", values.footer_nav],
    ["Footer company links", values.footer_company],
  ];
  for (const [label, list] of navFields) {
    if (!Array.isArray(list) || list.length === 0) {
      issues.push(`${label}: at least one link is required.`);
      continue;
    }
    list.forEach((item, index) => {
      const row = item as Record<string, unknown>;
      if (!nonEmptyString(row.href)) {
        issues.push(`${label} #${index + 1}: href is required.`);
      }
      requireLocaleMapStrings(`${label} #${index + 1} label`, row.label, issues);
    });
  }
  return issues;
}

export function collectPageCopyCompletenessIssues(
  values: Record<string, unknown>,
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = [];
  if (!nonEmptyString(values.page_key)) {
    issues.push("Page key: required.");
  }
  requireLocaleMapStrings("Page title", values.title, issues);
  requireLocaleMapStrings("Primary heading", values.heading, issues);
  requireLocaleMapStrings("Primary body", values.body, issues);
  return issues;
}

export function collectCommunityStoryCompletenessIssues(
  values: Record<string, unknown>,
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = [];
  requireLocaleMapStrings("Quote", values.quote, issues);
  if (!nonEmptyString(values.author_name)) {
    issues.push("Author name: required.");
  }
  if (!nonEmptyString(values.tier_label)) {
    issues.push("Tier label: required.");
  }
  requireLocaleMapStrings("Image alt text", values.alt_text, issues);
  return issues;
}

export function collectHotelCompletenessIssues(
  values: Record<string, unknown>,
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = [];
  if (!nonEmptyString(values.liteapi_hotel_id)) {
    issues.push("LiteAPI hotel ID: required.");
  }
  if (!nonEmptyString(values.destination_id)) {
    issues.push("Destination ID: required.");
  }
  if (!nonEmptyString(values.name)) {
    issues.push("Name: required.");
  }
  const content = values.content;
  const en =
    content && typeof content === "object" && !Array.isArray(content)
      ? (content as Record<string, unknown>).en
      : undefined;
  const enBlurb =
    en && typeof en === "object" && !Array.isArray(en)
      ? (en as Record<string, unknown>).blurb
      : undefined;
  if (!nonEmptyString(enBlurb)) {
    issues.push("Content (English): blurb is required.");
  }
  return issues;
}

function slideHasPlaceCaption(slide: Record<string, unknown>): boolean {
  const caption = String(slide.caption ?? "").trim();
  if (!caption) return false;
  const loc = slide.location;
  if (loc && typeof loc === "object" && !Array.isArray(loc)) {
    const L = loc as Record<string, unknown>;
    if (
      nonEmptyString(L.city) ||
      nonEmptyString(L.country) ||
      nonEmptyString(L.name)
    ) {
      return true;
    }
  }
  // Place-like: "City, Country" or a named region (not a bare mood phrase)
  if (/,/.test(caption) && caption.length >= 4) return true;
  if (
    /\b(alps|fjords|highlands|island|coast|valley|desert|sahara|patagonia|tuscany|provence)\b/i
      .test(caption)
  ) {
    return true;
  }
  // Single proper-looking place token (capitalized word, not mood jargon)
  if (
    /^[A-Z][\w'’\-]+(?:\s+[A-Z][\w'’\-]+){0,3}$/.test(caption) &&
    !/\b(Mood|Landscape|Dawn|Dusk|Mist|Forest|Ocean|Sky)\b/.test(caption)
  ) {
    return true;
  }
  return false;
}

export function collectHomeHeroSetCompletenessIssues(
  values: Record<string, unknown>,
): CompletenessIssue[] {
  const issues: CompletenessIssue[] = [];
  if (!nonEmptyString(values.title)) {
    issues.push("Title: required.");
  }
  const slides = values.slides;
  if (!Array.isArray(slides) || slides.length < 1) {
    issues.push("Slides: at least one slide is required.");
    return issues;
  }
  if (slides.length > 12) {
    issues.push("Slides: at most 12 slides.");
  }
  slides.forEach((raw, index) => {
    const slide = (raw ?? {}) as Record<string, unknown>;
    const label = `Slide #${index + 1}`;
    const mediaType = String(slide.media_type ?? "image");
    if (!nonEmptyString(slide.caption)) {
      issues.push(
        `${label}: caption is required (place name for trip inspiration).`,
      );
    } else if (!slideHasPlaceCaption(slide)) {
      issues.push(
        `${label}: caption must identify where the image is (e.g. “Lisbon, Portugal” or “Swiss Alps”), not a generic mood label.`,
      );
    }
    if (!nonEmptyString(slide.altText) && !nonEmptyString(slide.alt_text)) {
      issues.push(`${label}: alt text is required.`);
    }
    if (mediaType === "video_sequence") {
      if (!nonEmptyString(slide.videoUrl) && slide.pexelsVideoId == null) {
        issues.push(`${label}: video URL or Pexels video ID is required.`);
      }
      if (!nonEmptyString(slide.imageUrl) && !nonEmptyString(slide.posterUrl)) {
        issues.push(`${label}: poster / image URL is required for video slides.`);
      }
    } else if (!nonEmptyString(slide.imageUrl)) {
      issues.push(`${label}: image URL is required.`);
    }
  });
  return issues;
}

export type CompletenessCollector = (
  values: Record<string, unknown>,
) => CompletenessIssue[];

export function assertCompleteForReady(
  values: Record<string, unknown>,
  collect: CompletenessCollector,
  entityLabel = "item",
): void {
  const issues = collect(values);
  if (issues.length === 0) return;
  throw new Error(
    `Cannot set status to Ready for Publishing — ${entityLabel} is incomplete:\n` +
      issues.map((issue) => `• ${issue}`).join("\n"),
  );
}
